import React, { FC, useEffect } from 'react';
import { toast } from 'react-toastify';
import { useNavigate, useParams } from 'react-router-dom';
import Form from '../Form';
import {
  getChallengeDayImageSelector,
  getChallengeDaySelector,
  getChallengeDayVideoRuSelector,
  getChallengeDayVideoSelector,
  getDaysLoadingSelector,
  getIsImageLoadingSelector,
  getIsVideoLoadingSelector,
} from '../../selector';
import { useAppDispatch, useAppSelector } from '../../../../hooks';
import BackLink from '../../../../components/BackLink';
import {
  getChallengeDay,
  updateChallengeDay,
  uploadImage,
  uploadVideo,
} from '../../service';
import { IDayForm } from '../../types';
import { ToastMessage } from '../../../../utils/constants';
import { clearDay } from '../../slices';

const UpdateDay: FC = () => {
  const isLoading = useAppSelector(getDaysLoadingSelector);
  const isImageLoading = useAppSelector(getIsImageLoadingSelector);
  const isVideoLoading = useAppSelector(getIsVideoLoadingSelector);
  const loading = isLoading || isImageLoading || isVideoLoading;
  const challengeDay = useAppSelector(getChallengeDaySelector);
  const oldImage = useAppSelector(getChallengeDayImageSelector);
  const oldVideo = useAppSelector(getChallengeDayVideoSelector);
  const oldVideoRu = useAppSelector(getChallengeDayVideoRuSelector);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { taskId } = useParams();

  useEffect(() => {
    if (taskId) {
      dispatch(getChallengeDay({ id: taskId }));
    }
    return () => {
      dispatch(clearDay());
    };
  }, []);

  const checkForm = ({
    description,
    descriptionRu,
    name,
    nameRu,
    video,
    videoRu,
    image,
    duration,
    durationRu,
  }: IDayForm): boolean => {
    const isFormFull = !!(
      description &&
      name &&
      duration &&
      descriptionRu &&
      nameRu &&
      durationRu
    );
    const isImageAttached = !!(
      (image && image.length) ||
      (challengeDay?.image && challengeDay?.image.url)
    );
    const isVideoAttached = !!(
      (video && video.length) ||
      (challengeDay?.video && challengeDay?.video.url) ||
      (videoRu && videoRu.length) ||
      (challengeDay?.videoRu && challengeDay?.videoRu.url)
    );
    return isFormFull && isVideoAttached && isImageAttached;
  };

  const onSubmit = (form: IDayForm) => {
    const {
      description,
      descriptionRu,
      name,
      nameRu,
      video,
      videoRu,
      image,
      duration,
      durationRu,
    } = form;
    const isFormFull = checkForm(form);

    if (taskId && isFormFull) {
      dispatch(
        updateChallengeDay({
          id: taskId,
          description,
          descriptionRu,
          nameRu,
          name,
          videoDuration: duration && +duration,
          videoDurationRu: durationRu && +durationRu,
        }),
      ).then(({ meta }) => {
        if (meta.requestStatus === 'fulfilled') {
          dispatch(getChallengeDay({ id: taskId }));
        }
      });

      if (video && video.length) {
        const file = video[0];
        dispatch(
          uploadVideo({
            file,
            id: taskId,
            duration,
          }),
        );
      }
      if (videoRu && videoRu.length) {
        const file = videoRu[0];
        dispatch(
          uploadVideo({
            file,
            id: taskId,
            duration,
            lang: 'ru',
          }),
        );
      }
      if (image && image.length) {
        const file = image[0];
        dispatch(
          uploadImage({
            file,
            id: taskId,
          }),
        );
      }
    } else {
      toast.warning(ToastMessage.CHECK_ALL_FIELDS);
    }
  };
  const goBack = () => navigate(-1);

  return (
    <div>
      <BackLink text="Back to Tasks of the day" onClick={goBack} />
      <h1>{`Update Day ${challengeDay?.position || ''}`}</h1>
      {loading ? (
        <p>Loading...</p>
      ) : (
        <Form
          onSubmit={onSubmit}
          form={{
            name: challengeDay?.name || '',
            nameRu: challengeDay?.nameRu || '',
            description: challengeDay?.description || '',
            descriptionRu: challengeDay?.descriptionRu || '',
            duration: challengeDay?.duration || 0,
            durationRu: challengeDay?.durationRu || 0,
          }}
          oldImage={oldImage}
          oldVideoPreview={oldVideo}
          oldVideoPreviewRu={oldVideoRu}
          position={challengeDay?.position || ''}
        />
      )}
    </div>
  );
};

export default UpdateDay;
